import React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Button, Typography as SharedTypography } from '@universal-tennis/ui-shared';
import moment from 'moment/moment';
import VerifiedIcon from '../../../assets/icons/icon-verified-blue.svg';
import UtrIcon from '../../../components/Icons/UtrIcon';
import { ageDisplay, getAgeKeys, getFormattedConferenceRange } from '../../../utils/display';
import { genderTypes, getTypeForId, RequestStatusTypes, LeagueSessionTypes, StatusTypes } from '../../../utils/types';
import { LeagueTabs } from '../../../utils/constants';

const SessionPageHeader = ({ sessionData, tab, onTabChange, onRequestToCaptain, onRegisterButtonClick }) => {
    const { conference, session, captainRequest } = sessionData;
    const { conferenceRatings, sportTypeId } = conference || {};
    const { currentRequest, isCaptainInSession } = captainRequest || {};
    const { PENDING } = RequestStatusTypes;
    const { OVERVIEW, PLAYERS, SCHEDULE, DRAW } = LeagueTabs;
    const ageKeys = getAgeKeys([conference.minAge, conference.maxAge]);
    const ageRange = ageDisplay(ageKeys);
    const ages = ageRange?.toLowerCase() === 'all' ? 'all ages' : ageRange;
    const gender = getTypeForId(conference.genderTypeId, genderTypes);
    const location = conference?.location?.placeName ? `${conference?.location?.placeName} \u2022` : '';
    const range = getFormattedConferenceRange({ conferenceRatings, sportTypeId, showRatingPrefix: true });
    const sessionSummary = `${location} ${ages} \u2022 ${range} \u2022 ${gender.description}`;
    const formattedStartDate = moment(session.teamRegistrationStartDateUtc).format('MMMM Do');
    const formattedEndDate = moment(session.teamRegistrationEndDateUtc).format('MMMM Do');
    const TEAMS_FORMING_TEXT = `Teams are forming now. Register between ${formattedStartDate} - ${formattedEndDate}.`;
    const TEAMS_FORMING_CAPTAIN_TEXT = `${TEAMS_FORMING_TEXT} Interested in being captain?`;
    const MATCH_TAB = session?.sessionTypeId === LeagueSessionTypes.TOURNAMENT ? DRAW : SCHEDULE;

    const today = moment.utc();
    const isPlayerInSession = sessionData?.memberTeams?.length !== 0;
    const isCaptainRequestPending = currentRequest?.sessionRequestStatusTypeId === PENDING;
    const isTodayBeforeSessionEndDate = today.isBefore(session?.endDateUtc, 'day');
    const isTodayBeforeRegistrationEndDate = today.isBefore(session.teamRegistrationEndDateUtc, 'day');
    const isNotAPlayerOrCaptainInSession = !isPlayerInSession && !isCaptainInSession;
    const showRequestToCaptainButton = isTodayBeforeRegistrationEndDate && !isCaptainRequestPending && isNotAPlayerOrCaptainInSession;
    const showTeamsFormingText = (isTodayBeforeRegistrationEndDate && isNotAPlayerOrCaptainInSession) || session?.sessionStatusTypeId === StatusTypes.CLOSED;
    const showRegisterButton = isTodayBeforeSessionEndDate && isNotAPlayerOrCaptainInSession;

    return (
        <>
            <Box pt={10}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Stack spacing={2}>
                            <Grid container wrap="nowrap">
                                <Grid item xs={8}>
                                    <Typography variant="caption">{conference.name}</Typography>
                                    <Typography pt={3} pb={3} textTransform="none" variant="h2">
                                        {session?.name}
                                    </Typography>
                                    <Typography variant="caption">{sessionSummary?.toUpperCase()}</Typography>
                                </Grid>
                                <Grid sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }} item xs={4}>
                                    {showRegisterButton && (
                                        <>
                                            <Button onClick={onRegisterButtonClick} category="primary-expanded">REGISTER</Button>
                                            {showTeamsFormingText && (
                                                <SharedTypography style={{ paddingTop: '24px' }} size="small-book" category="secondary">
                                                    {isCaptainRequestPending ? TEAMS_FORMING_TEXT : TEAMS_FORMING_CAPTAIN_TEXT}
                                                </SharedTypography>
                                            )}
                                        </>
                                    )}
                                    {isPlayerInSession && isTodayBeforeSessionEndDate && (
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <UtrIcon icon={VerifiedIcon} size="16px" alt="verified" style={{ marginRight: 5 }} />
                                            <SharedTypography size="small-book" category="secondary">
                                                You&apos;re Registered for teams.
                                            </SharedTypography>
                                        </Box>
                                    )}
                                    <Box mt={1}>
                                        {showRequestToCaptainButton && (
                                            <SharedTypography size="small-book" category="secondary">
                                                <>
                                                    <Button onClick={onRequestToCaptain} style={{ padding: 0, paddingLeft: isPlayerInSession ? '4px' : 0, backgroundColor: 'transparent' }} type="link">Request to Captain</Button>
                                                </>
                                            </SharedTypography>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <Box mt={5}>
                <Tabs value={tab} onChange={onTabChange}>
                    <Tab label={OVERVIEW} value={OVERVIEW} />
                    <Tab label={MATCH_TAB} value={MATCH_TAB} />
                    <Tab label={PLAYERS} value={PLAYERS} />
                </Tabs>
            </Box>
        </>
    );
};
SessionPageHeader.propTypes = {};
export default SessionPageHeader;
